import axios, { AxiosInstance, AxiosError } from 'axios';

/**
 * ggc-be v2 인플루언서 API 클라이언트
 *
 * baseURL: /influencer/ (dev — vite proxy가 api-staging.ggcontent.com으로 라우팅)
 *          https://api.ggcontent.com/influencer/ (prod build 기본값)
 *          VITE_NEW_API_BASE_URL이 있으면 그 값 우선
 *
 * 응답 엔벨로프 자동 언래핑: { success: boolean, data: T, message?, errorCode? }
 * 에러 형식: { success: false, message: string, errorCode?: string }
 */

interface ApiEnvelope<T> {
  success: boolean;
  data: T;
  message?: string;
  errorCode?: string;
}

interface ApiError {
  message: string;
  code?: number;
  isProcessed?: boolean;
}

type ErrorDispatcher = (error: ApiError) => void;

let errorDispatcher: ErrorDispatcher = (error) => {
  console.error('[influencerApi]', error.message);
};

function isStagingBackend(): boolean {
  if (typeof window === 'undefined') return false;
  return window.location.hostname === 'influencer2-staging.ggcontent.com';
}

function resolveBaseUrl(): string {
  if (import.meta.env.VITE_NEW_API_BASE_URL) {
    return `${import.meta.env.VITE_NEW_API_BASE_URL}/influencer/`;
  }
  if (import.meta.env.PROD) {
    if (isStagingBackend()) return 'https://api-staging.ggcontent.com/influencer/';
    return 'https://api.ggcontent.com/influencer/';
  }
  return '/influencer/';
}

const baseURL = resolveBaseUrl();

const client: AxiosInstance = axios.create({
  baseURL,
  timeout: 120 * 1000,
  withCredentials: true,
  responseType: 'json',
});

function processResponse<T>(response: { data: ApiEnvelope<T> }): T {
  return response.data.data;
}

function processError(err: AxiosError): Promise<never> {
  const response = err.response;
  let error: ApiError;

  if (response) {
    const data = response.data as {
      message?: string;
      errorCode?: string;
      error?: ApiError;
    } | undefined;
    error = data?.error ?? {
      message: data?.message ?? err.message,
      code: response.status,
    };

    if (response.status === 401) {
      window.location.href = '/login';
      return Promise.reject(error);
    }
  } else {
    error = { message: err.toString() };
  }

  errorDispatcher(error);
  error.isProcessed = true;
  return Promise.reject(error);
}

export const influencerApi = {
  post<T = unknown>(resource: string, data: Record<string, unknown> = {}): Promise<T> {
    return client.post<ApiEnvelope<T>>(resource, data)
      .then(processResponse)
      .catch(processError);
  },

  get<T = unknown>(resource: string, params?: Record<string, unknown>): Promise<T> {
    return client.get<ApiEnvelope<T>>(resource, { params })
      .then(processResponse)
      .catch(processError);
  },

  postForm<T = unknown>(resource: string, form: FormData): Promise<T> {
    return client.post<ApiEnvelope<T>>(resource, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
      .then(processResponse)
      .catch(processError);
  },

  setErrorDispatcher(dispatcher: ErrorDispatcher): void {
    errorDispatcher = dispatcher;
  },

  getClient(): AxiosInstance {
    return client;
  },
};

export default influencerApi;
